import React, { useEffect, useState } from 'react';
import { UserProfileData } from '../../hooks/useUserProfile';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  profileData: UserProfileData;
  onSubmit: (data: Partial<UserProfileData>) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

const ProfileEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  profileData,
  onSubmit,
  isLoading,
  error,
  successMessage,
}) => {
  const [formData, setFormData] = useState<Partial<UserProfileData>>(profileData);

  useEffect(() => {
    setFormData(profileData);
  }, [profileData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="profile-preview-modal" role="dialog" aria-modal="true" aria-labelledby="profile-edit-title">
      <div className="profile-preview-modal__backdrop" onClick={onClose} aria-hidden />
      <div className="profile-preview-modal__panel">
        <div className="profile-preview-modal__header">
          <h2 id="profile-edit-title">Edit Profile</h2>
          <button type="button" onClick={onClose} className="profile-preview-modal__close" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="profile-preview-modal__form">
          <div className="profile-preview-modal__field">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username || ''}
              onChange={handleChange}
              required
            />
          </div>

          <div className="profile-preview-modal__field">
            <label htmlFor="instagram">Instagram Handle (optional)</label>
            <input
              type="text"
              id="instagram"
              name="instagram"
              value={formData.instagram || ''}
              onChange={handleChange}
              placeholder="@username or username"
            />
            <p className="profile-preview-modal__hint">
              Enter just the handle (e.g., &quot;therealashish___&quot;) or full Instagram URL
            </p>
          </div>

          <div className="profile-preview-modal__field">
            <label htmlFor="youtube">YouTube Channel (optional)</label>
            <input
              type="text"
              id="youtube"
              name="youtube"
              value={formData.youtube || ''}
              onChange={handleChange}
              placeholder="Channel name or full URL"
            />
            <p className="profile-preview-modal__hint">
              Enter channel name (e.g., &quot;channelname&quot;) or full YouTube URL
            </p>
          </div>

          {error && <div className="profile-preview-modal__alert profile-preview-modal__alert--error">{error}</div>}
          {successMessage && (
            <div className="profile-preview-modal__alert profile-preview-modal__alert--success">{successMessage}</div>
          )}

          <div className="profile-preview-modal__actions">
            <button type="button" onClick={onClose} className="profile-preview-modal__btn profile-preview-modal__btn--ghost">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="profile-preview-modal__btn profile-preview-modal__btn--primary"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileEditModal;
